import React from "react";
import { useLanguage, T } from "@/lib/i18n";
import { ArrowRight, ChevronRight } from "lucide-react";

export default function Hero() {
  const { lang } = useLanguage();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative bg-white border-b border-sjm-line overflow-hidden"
    >
      <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 sm:pt-24 lg:pt-32 pb-20 sm:pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-sjm-red text-sjm-red text-xs font-bold uppercase tracking-[0.2em]">
              <span className="w-1.5 h-1.5 bg-sjm-red rounded-full animate-pulse" />
              {T.hero.badge[lang]}
            </div>

            <h1
              className={`mt-6 font-display font-extrabold tracking-tighter leading-[0.92] text-sjm-ink text-5xl sm:text-7xl lg:text-[7.5rem] ${lang === "bn" ? "font-bn" : ""}`}
            >
              {lang === "bn" ? T.hero.title_bn : T.hero.title_en}
            </h1>

            <p
              className={`mt-8 max-w-2xl text-lg sm:text-xl text-sjm-muted leading-relaxed ${lang === "bn" ? "font-bn" : ""}`}
            >
              {T.hero.subtitle[lang]}
            </p>

            <div className="mt-10 flex flex-wrap gap-3 fade-up fade-up-2">
              <button
                data-testid="hero-cta-join"
                onClick={() => scrollTo("join")}
                className="group inline-flex items-center gap-2 px-6 py-4 bg-sjm-red text-white font-bold uppercase tracking-wider text-sm hover:bg-[#D62828] transition-all"
              >
                {T.hero.cta_join[lang]}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-cta-campaigns"
                onClick={() => scrollTo("campaigns")}
                className="group inline-flex items-center gap-2 px-6 py-4 border-2 border-sjm-ink text-sjm-ink font-bold uppercase tracking-wider text-sm hover:bg-sjm-ink hover:text-white transition-all"
              >
                {T.nav.campaigns[lang]}
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-4 fade-up fade-up-3">
            <div className="relative h-full min-h-[280px] lg:min-h-[420px] border border-sjm-line bg-sjm-mist overflow-hidden">
              <img
                src="https://assets.telegraphindia.com/abp/2023/Dec/1703378133_protest.jpg"
                alt="Protest march"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sjm-ink/80 via-sjm-ink/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs uppercase tracking-[0.2em] text-sjm-red mb-2 font-bold">
                  {lang === "bn" ? "মাঠে আমাদের" : "On the Ground"}
                </div>
                <div
                  className={`font-display font-bold text-2xl ${lang === "bn" ? "font-bn" : ""}`}
                >
                  {lang === "bn"
                    ? "রাজপথ থেকে আদালত — আমরা সর্বত্র।"
                    : "From the streets to the courts — we are everywhere."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complaint banner */}
      <div className="bg-sjm-red text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <div className={`font-bold ${lang === "bn" ? "font-bn" : ""}`}>{T.hero.complaint_email[lang]}</div>
          <div className={`${lang === "bn" ? "font-bn" : ""} text-sm`}>{T.hero.complaint_note[lang]}</div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="border-t border-sjm-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-5 divide-x divide-sjm-line">
          {[
            {
              v: "50+",
              l: lang === "bn" ? "অনুমোদিত সংগঠন" : "Affiliated unions",
            },
            { v: "23", l: lang === "bn" ? "জেলা সক্রিয়" : "Districts active" },
            {
              v: "1.2M+",
              l: lang === "bn" ? "কর্মী প্রতিনিধিত্ব" : "Workers represented",
            },
            { v: "100%", l: lang === "bn" ? "অহিংস" : "Non-violent" },
          ].map((s, i) => (
            <div key={i} className="py-8 px-6 first:pl-0">
              <div className="font-display font-extrabold text-3xl sm:text-4xl text-sjm-ink">
                {s.v}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-sjm-muted">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
